import {
  isReferralCode,
  lineEntryHref,
  referralCodeFromCookieHeader,
  referralCodeFromUrl,
  referralCookieSecure,
  referralSetCookie,
  REFERRAL_COOKIE_MAX_AGE,
  REFERRAL_STORAGE_KEY,
} from "./referral-landing";

/**
 * localStorage の紹介コードの期限。shegate_ref_code は /women の HP と LP が素の文字列で読むので、
 * 期限は別の名前で横に持つ。どのコードの期限かを持つよう「コード:エポックのミリ秒」の形にする
 * (/lab の受け取り apps/lab-astro/src/client/referral.ts も同じ形で書く)。cookie と同じ 30 日で、後から来たコードが置き直す
 */
export const REFERRAL_STORAGE_EXPIRES_KEY = "yumesaku_ref_expires";

export function referralExpiresMark(code: string, now: number): string {
  return `${code}:${now + REFERRAL_COOKIE_MAX_AGE * 1000}`;
}

/** 期限の印が、このコードのものなら期限を返す。別のコードの印や壊れた印は null */
function expiresFor(mark: string | null | undefined, code: string): number | null {
  if (!mark) return null;
  const at = mark.lastIndexOf(":");
  if (at === -1 || mark.slice(0, at).toUpperCase() !== code) return null;
  const expires = Number(mark.slice(at + 1));
  return Number.isFinite(expires) && expires > 0 ? expires : null;
}

function readStorage(storage: Storage | null, now: number): string | null {
  try {
    const raw = storage?.getItem(REFERRAL_STORAGE_KEY) ?? null;
    if (!isReferralCode(raw)) return null;
    const value = raw.toUpperCase();
    const expires = expiresFor(storage?.getItem(REFERRAL_STORAGE_EXPIRES_KEY), value);
    if (expires === null) {
      // /women の頁が期限なしで置いた値か、別のコードの印。初めて見た時から 30 日にする
      storage?.setItem(REFERRAL_STORAGE_EXPIRES_KEY, referralExpiresMark(value, now));
    } else if (expires <= now) {
      storage?.removeItem(REFERRAL_STORAGE_KEY);
      storage?.removeItem(REFERRAL_STORAGE_EXPIRES_KEY);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

function writeStorage(storage: Storage | null, code: string, now: number): void {
  try {
    storage?.setItem(REFERRAL_STORAGE_KEY, code);
    storage?.setItem(REFERRAL_STORAGE_EXPIRES_KEY, referralExpiresMark(code, now));
  } catch {
    // private の窓など、書けないときは写さない。cookie だけで決済と LINE の入口は動く
  }
}

/**
 * cookie と localStorage の紹介コードをそろえる。どちらが新しいかの決めは次の通り。
 * - 今の住所に ?ref= があれば、それがいちばん新しい (worker が同じ応答で cookie に置いた値)。localStorage にも写す
 * - 無ければ localStorage が勝つ。/women の HP と LP (別の worker) は ?ref= を localStorage にだけ置き、
 *   本体の頁で ?ref= を受けたときは同じ訪問で localStorage にも写すので、localStorage は cookie と同じか新しい
 * - localStorage に無く cookie にだけあれば、localStorage に写す
 * - localStorage の値は期限 (30 日) を横に持ち、切れていれば捨てる
 * そろえたコードを返す
 */
export function syncReferralCode(
  doc: Document,
  storage: Storage | null,
  href: string,
  now: number = Date.now(),
): string | null {
  let fromUrl: string | null = null;
  try {
    fromUrl = referralCodeFromUrl(new URL(href));
  } catch {
    fromUrl = null;
  }
  const fromCookie = referralCodeFromCookieHeader(doc.cookie);
  const fromStorage = readStorage(storage, now);
  const code = fromUrl ?? fromStorage ?? fromCookie;
  if (!code) return null;
  // 住所の ?ref= は同じコードでも期限を置き直す (cookie の Max-Age と同じく、来るたびに 30 日)
  if (fromUrl || fromStorage !== code) writeStorage(storage, code, now);
  if (fromCookie !== code) {
    doc.cookie = referralSetCookie(code, referralCookieSecure(new URL(href).hostname));
  }
  return code;
}

/**
 * 画面を開いたときに 1 回呼ぶ。紹介コードを cookie と localStorage でそろえ、
 * LINE の入口のリンクを押した時に LIFF へ替える。外す関数を返す
 */
export function installReferralLanding(doc: Document, storage: Storage | null, href: string): () => void {
  syncReferralCode(doc, storage, href);
  const onClick = (event: Event) => {
    const target = event.target as Element | null;
    const anchor = target?.closest?.("a[href]");
    if (!anchor) return;
    const current = anchor.getAttribute("href") ?? "";
    const next = lineEntryHref(current, referralCodeFromCookieHeader(doc.cookie) ?? readStorage(storage, Date.now()));
    if (next !== current) anchor.setAttribute("href", next);
  };
  // 中の釦で新しいタブに開くときは click でなく auxclick が来る
  doc.addEventListener("click", onClick, { capture: true });
  doc.addEventListener("auxclick", onClick, { capture: true });
  return () => {
    doc.removeEventListener("click", onClick, { capture: true });
    doc.removeEventListener("auxclick", onClick, { capture: true });
  };
}
